import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const UNITS = ["PCS", "SET", "BOX", "ROLL", "METER", "LITER", "KG", "BAG", "PACK", "PAIR", "LOT", ];

// ── Field accessor — handles exact API casing: control_No, item_Name, etc. ──
const f = {
    controlNo: r => r.control_No || r.Control_No || r.controlNo || "",
    department: r => r.department || r.Department || "",
    date: r => r.date || r.Date || "",
    preparedBy: r => r.preparedBy || r.PreparedBy || "",
    itemName: r => r.item_Name || r.Item_Name || r.itemName || "",
    quantity: r => r.quantity || r.Quantity || "",
    unit: r => r.unit || r.Unit || "",
    description: r => r.description || r.Description || "",
    remarks: r => r.remarks || r.Remarks || "",
    deptHead: r => r.department_Head || r.Department_Head || r.departmentHead || "",
    approvedBy: r => r.approved_By || r.Approved_By || r.approvedBy || "",
    receivedBy: r => r.item_ReceivedBy || r.Item_ReceivedBy || r.itemReceivedBy || "",
    created: r => r.created || r.Created || "",
    isApproved: r => r.isApproved ?? r.IsApproved ?? null,
    isDisapproved: r => r.isDisapproved ?? r.IsDisapproved ?? null,
};

// ── Status helpers ───────────────────────────────────────────────────────────
const getStatus = (row) => {
    if (f.isApproved(row) === true) return "approved";
    if (f.isDisapproved(row) === true) return "disapproved";
    return "pending";
};

const STATUS_CONFIG = {
    approved: { label: "Approved", color: "#16a34a", bg: "#dcfce7", border: "#86efac", icon: "✅" },
    disapproved: { label: "Disapproved", color: "#dc2626", bg: "#fee2e2", border: "#fca5a5", icon: "❌" },
    pending: { label: "Pending", color: "#d97706", bg: "#fef9c3", border: "#fde68a", icon: "⏳" },
};

const StatusBadge = ({ row }) => {
    const status = getStatus(row);
    const cfg = STATUS_CONFIG[status];
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            backgroundColor: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.border}`,
            borderRadius: "20px", padding: "3px 10px",
            fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap",
        }}>
            {cfg.icon} {cfg.label}
        </span>
    );
};

// ══════════════════════════════════════════════════════════════════
// ── VIEW MODAL — Official Disposal Form Layout ────────────────────
// ══════════════════════════════════════════════════════════════════
const ViewModal = ({ row, onClose }) => {
    if (!row) return null;

    // Parse comma-separated items back into rows
    const quantities = (f.quantity(row) || "").split(",").map(s => s.trim());
    const units = (f.unit(row) || "").split(",").map(s => s.trim());
    const itemNames = (f.itemName(row) || "").split(",").map(s => s.trim());
    const descriptions = (f.description(row) || "").split(",").map(s => s.trim());
    const remarksList = (f.remarks(row) || "").split(",").map(s => s.trim());
    const itemCount = Math.max(quantities.length, itemNames.length, 1);
    const items = Array.from({ length: itemCount }, (_, i) => ({
        quantity: quantities[i] || "",
        unit: units[i] || "",
        itemName: itemNames[i] || "",
        description: descriptions[i] || "",
        remarks: remarksList[i] || "",
    }));

    const formDate = f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
    }) : "—";

    const status = getStatus(row);
    const statusCfg = STATUS_CONFIG[status];

    // ── Styles scoped to modal ──
    const tdStyle = {
        border: "1px solid #333",
        padding: "6px 8px",
        fontSize: "12px",
        fontFamily: "'Times New Roman', serif",
    };
    const thStyle = {
        ...tdStyle,
        backgroundColor: "#f0f0f0",
        fontWeight: "700",
        textAlign: "center",
        fontSize: "11px",
        textTransform: "uppercase",
    };
    const labelStyle = {
        fontSize: "10px",
        fontWeight: "700",
        color: "#444",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    };
    const sigLineStyle = {
        borderBottom: "1px solid #333",
        textAlign: "center",
        padding: "4px 8px",
        fontSize: "13px",
        fontWeight: "700",
        color: "#1a1a1a",
        minHeight: "28px",
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0,
                backgroundColor: "rgba(0,0,0,0.55)",
                display: "flex", alignItems: "flex-start",
                justifyContent: "center",
                padding: "24px 16px",
                zIndex: 9999,
                overflowY: "auto",
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    backgroundColor: "#fff",
                    width: "100%",
                    maxWidth: "860px",
                    border: "2px solid #333",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                    fontFamily: "'Times New Roman', serif",
                    borderRadius: "2px",
                }}
            >
                {/* ── Modal Action Bar ── */}
                <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 16px",
                    backgroundColor: "#1e40af",
                    borderBottom: "2px solid #333",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ color: "#fff", fontWeight: "700", fontSize: "14px", fontFamily: "Arial, sans-serif" }}>
                            📄 Disposal Form Details
                        </span>
                        <span style={{
                            backgroundColor: statusCfg.bg, color: statusCfg.color,
                            border: `1px solid ${statusCfg.border}`,
                            borderRadius: "20px", padding: "2px 10px",
                            fontSize: "11px", fontWeight: "700",
                        }}>
                            {statusCfg.icon} {statusCfg.label}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "rgba(255,255,255,0.2)", color: "#fff",
                            border: "1px solid rgba(255,255,255,0.4)",
                            borderRadius: "6px", padding: "5px 14px",
                            cursor: "pointer", fontSize: "13px", fontWeight: "700",
                            fontFamily: "Arial, sans-serif",
                        }}
                    >✕ Close</button>
                </div>

                {/* ── TOP HEADER ── */}
                <table style={{ width: "100%", borderCollapse: "collapse", borderBottom: "2px solid #333" }}>
                    <tbody>
                        <tr>
                            {/* Logo + Company */}
                            <td style={{ ...tdStyle, width: "50%", borderTop: "none", borderLeft: "none", verticalAlign: "middle", padding: "12px 16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <div style={{
                                        width: "48px", height: "48px", borderRadius: "50%",
                                        background: "linear-gradient(135deg, #1a6fb5, #0d4a80)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        color: "white", fontWeight: "900", fontSize: "13px", flexShrink: 0,
                                        fontFamily: "Arial, sans-serif",
                                    }}>MS</div>
                                    <div>
                                        <div style={{ fontWeight: "900", fontSize: "14px", fontFamily: "Arial Black, sans-serif", color: "#1a1a1a" }}>
                                            MEGA SOFT Hygienic Products, Inc.
                                        </div>
                                        <div style={{ fontSize: "10px", color: "#555", fontFamily: "Arial, sans-serif" }}>
                                            34 P. Jacinto, Pleasant View Drive, Brgy. 165 Bagbaguin Caloocan City
                                        </div>
                                    </div>
                                </div>
                            </td>
                            {/* Control No */}
                            <td style={{ ...tdStyle, borderTop: "none", borderRight: "none", verticalAlign: "middle", padding: "12px 16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span style={{ ...labelStyle, whiteSpace: "nowrap" }}>CONTROL NO:</span>
                                    <span style={{ fontWeight: "900", fontSize: "15px", color: "#1a6fb5", fontFamily: "Arial, sans-serif" }}>
                                        {f.controlNo(row) || "—"}
                                    </span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── DOCUMENT META ── */}
                <table style={{ width: "100%", borderCollapse: "collapse", borderBottom: "2px solid #333" }}>
                    <tbody>
                        <tr>
                            <td style={{ ...tdStyle, borderLeft: "none", width: "20%", backgroundColor: "#f5f5f5" }}>
                                <div style={labelStyle}>Document Code</div>
                                <div style={{ fontSize: "11px", fontWeight: "600" }}>SPR-F-04-DIS-004</div>
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center", fontWeight: "900", fontSize: "20px", letterSpacing: "1px", color: "#1a1a1a", fontFamily: "Arial Black, sans-serif" }} colSpan={5}>
                                ITEM DISPOSAL FORM
                            </td>
                            <td style={{ ...tdStyle, borderRight: "none", width: "1px", padding: 0 }}></td>
                        </tr>
                        <tr>
                            <td style={{ ...tdStyle, borderLeft: "none", backgroundColor: "#f5f5f5" }}>
                                <div style={labelStyle}>Document Owner</div>
                                <div style={{ fontSize: "11px" }}>SPAREPARTS DEPT</div>
                            </td>
                            <td style={{ ...tdStyle, backgroundColor: "#f5f5f5", textAlign: "center" }}>
                                <div style={labelStyle}>Issue No</div>
                                <div style={{ fontSize: "11px", fontWeight: "600" }}>1</div>
                            </td>
                            <td style={{ ...tdStyle, backgroundColor: "#f5f5f5", textAlign: "center" }}>
                                <div style={labelStyle}>Revision No.</div>
                                <div style={{ fontSize: "11px", fontWeight: "600" }}>0</div>
                            </td>
                            <td style={{ ...tdStyle, backgroundColor: "#f5f5f5", textAlign: "center" }}>
                                <div style={labelStyle}>Revision Date</div>
                                <div style={{ fontSize: "11px" }}>NONE</div>
                            </td>
                            <td style={{ ...tdStyle, backgroundColor: "#f5f5f5", textAlign: "center" }}>
                                <div style={labelStyle}>Effectivity Date</div>
                                <div style={{ fontSize: "11px" }}>February, 2026</div>
                            </td>
                            <td style={{ ...tdStyle, backgroundColor: "#f5f5f5", textAlign: "center" }}>
                                <div style={labelStyle}>No. of Pages</div>
                                <div style={{ fontSize: "11px" }}>1</div>
                            </td>
                            <td style={{ ...tdStyle, borderRight: "none", backgroundColor: "#f5f5f5", textAlign: "center" }}>
                                <div style={labelStyle}>Page</div>
                                <div style={{ fontSize: "11px" }}>1</div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={{ padding: "12px 16px 0 16px" }}>

                    {/* Department */}
                    <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={labelStyle}>DEPARTMENT:</span>
                        <span style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>
                            {f.department(row) || "—"}
                        </span>
                    </div>

                    {/* Items Table */}
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "8px" }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, width: "10%" }}>Quantity</th>
                                <th style={{ ...thStyle, width: "10%" }}>Unit</th>
                                <th style={{ ...thStyle, width: "30%" }}>Item Name</th>
                                <th style={{ ...thStyle, width: "25%" }}>Description</th>
                                <th style={{ ...thStyle, width: "25%" }}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={idx}>
                                    <td style={{ ...tdStyle, textAlign: "center" }}>{item.quantity || ""}</td>
                                    <td style={{ ...tdStyle, textAlign: "center" }}>{item.unit || ""}</td>
                                    <td style={tdStyle}>{item.itemName || ""}</td>
                                    <td style={tdStyle}>{item.description || ""}</td>
                                    <td style={tdStyle}>{item.remarks || ""}</td>
                                </tr>
                            ))}
                            {/* Empty rows to fill up to at least 5 rows */}
                            {items.length < 5 && Array.from({ length: 5 - items.length }).map((_, i) => (
                                <tr key={`empty-${i}`}>
                                    {[...Array(5)].map((_, j) => (
                                        <td key={j} style={{ ...tdStyle, height: "26px" }}></td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Nothing Follows */}
                    <div style={{
                        textAlign: "center", fontWeight: "700", fontSize: "12px",
                        padding: "7px", borderTop: "1px solid #ccc",
                        borderBottom: "1px solid #ccc", marginBottom: "14px",
                        fontFamily: "'Times New Roman', serif",
                    }}>
                        **NOTHING FOLLOWS**
                    </div>

                    {/* Prepared By + Date */}
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0" }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: "6px 0", width: "18%" }}>
                                    <span style={labelStyle}>PREPARED BY:</span>
                                </td>
                                <td style={{ padding: "6px 16px", width: "35%" }}>
                                    <div style={sigLineStyle}>{f.preparedBy(row) || "—"}</div>
                                </td>
                                <td style={{ padding: "6px 16px", width: "10%", textAlign: "right" }}>
                                    <span style={labelStyle}>Date:</span>
                                </td>
                                <td style={{ padding: "6px 0" }}>
                                    <div style={sigLineStyle}>{formDate}</div>
                                </td>
                            </tr>
                            <tr>
                                <td></td>
                                <td style={{ textAlign: "center", fontSize: "10px", color: "#555", paddingBottom: "10px", fontFamily: "'Times New Roman', serif" }}>
                                    Signature over Printed Name
                                </td>
                                <td colSpan={2}></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Department Head Disposition */}
                    <div style={{
                        border: "2px solid #333", padding: "8px 16px", marginBottom: "0",
                        borderLeft: "none", borderRight: "none",
                    }}>
                        <div style={{ textAlign: "center", fontWeight: "700", fontSize: "11px", marginBottom: "6px", fontFamily: "Arial, sans-serif" }}>
                            Department Head Disposition
                        </div>
                        <div style={{ fontSize: "16px", fontWeight: "900", textAlign: "center", fontFamily: "Arial Black, sans-serif", letterSpacing: "0.5px" }}>
                            □ For Disposal/Scrap &nbsp;&nbsp; □ Transfer Area/Location &nbsp;&nbsp; □ Remain at SPWH
                        </div>
                    </div>

                    {/* Dept Head + Approved By + Received By */}
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0", borderBottom: "1px solid #ccc" }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: "8px 0", width: "18%" }}>
                                    <span style={labelStyle}>Department Head:</span>
                                </td>
                                <td style={{ padding: "8px 16px", width: "32%" }}>
                                    <div style={sigLineStyle}>{f.deptHead(row) || "—"}</div>
                                </td>
                                <td style={{ padding: "8px 16px", width: "15%", textAlign: "right" }}>
                                    <span style={labelStyle}>Approved by:</span>
                                </td>
                                <td style={{ padding: "8px 0" }}>
                                    <div style={sigLineStyle}>{f.approvedBy(row) || "—"}</div>
                                </td>
                            </tr>
                            <tr>
                                <td></td>
                                <td style={{ textAlign: "center", fontSize: "10px", color: "#555", paddingBottom: "10px", fontFamily: "'Times New Roman', serif" }}>
                                    Signature over Printed Name
                                </td>
                                <td colSpan={2}></td>
                            </tr>
                            <tr>
                                <td style={{ padding: "6px 0", width: "18%" }}>
                                    <span style={labelStyle}>Item Received by:</span>
                                </td>
                                <td style={{ padding: "6px 16px" }}>
                                    <div style={sigLineStyle}>{f.receivedBy(row) || "—"}</div>
                                </td>
                                <td colSpan={2}></td>
                            </tr>
                            <tr>
                                <td></td>
                                <td style={{ textAlign: "center", fontSize: "10px", color: "#555", paddingBottom: "10px", fontFamily: "'Times New Roman', serif" }}>
                                    Scrap Officer In-Charge
                                </td>
                                <td colSpan={2}></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Footer disclaimer */}
                    <div style={{
                        textAlign: "center", fontSize: "10px", color: "#444",
                        padding: "7px 0", borderTop: "1px solid #aaa",
                        fontStyle: "italic", fontFamily: "'Times New Roman', serif",
                    }}>
                        <u>This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</u>
                    </div>

                    {/* Close Button */}
                    <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0 14px 0" }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: "9px 28px", borderRadius: "6px",
                                border: "1.5px solid #d1d5db", backgroundColor: "#fff",
                                color: "#374151", fontWeight: "700", cursor: "pointer",
                                fontSize: "13px", fontFamily: "Arial, sans-serif",
                            }}
                        >Close</button>
                    </div>

                </div>
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════
const Disposal_Form = () => {
    // ── Current User from localStorage ──────────────────────────────────────
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) {
            try { setCurrentUser(JSON.parse(stored)); }
            catch { setCurrentUser(null); }
        }
    }, []);

    const userName =
        currentUser?.fullName ||
        currentUser?.name ||
        currentUser?.Name ||
        currentUser?.username ||
        "Unknown User";

    const today = new Date().toLocaleDateString("en-US", {
        month: "2-digit", day: "2-digit", year: "numeric",
    });

    // ── State ────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("form");
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);

    const [disposalList, setDisposalList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [filterDept, setFilterDept] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // ── View Modal State ─────────────────────────────────────────────────────
    const [viewRow, setViewRow] = useState(null);

    const [form, setForm] = useState({
        Control_No: generateControlNo(),
        Department: "",
        Date: new Date().toISOString().split("T")[0],
        items: [{ Quantity: "", Unit: "", Item_Name: "", Description: "", Remarks: "" }],
        Department_Head: "",
        Item_ReceivedBy: "",
        Approved_By: "Justin Choi",

    });

    // NEW — fetch last control no from API, increment sequentially
    function generateControlNo() {
        return ""; // placeholder lang, real one fetched via useEffect below
    }

    // ── Fetch latest control no & generate next sequential ──────────────────
    useEffect(() => {
        const fetchNextControlNo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/DisposalForm`);
                if (res.ok) {
                    const data = await res.json();
                    const year = String(new Date().getFullYear()).slice(-2); // "26"

                    // Filter only this year's records matching "26-XXXXXX"
                    const thisYearNos = data
                        .map(r => f.controlNo(r))
                        .filter(n => n && n.startsWith(`${year}-`))
                        .map(n => parseInt(n.split("-")[1]) || 0);

                    const nextNum = thisYearNos.length > 0
                        ? Math.max(...thisYearNos) + 1
                        : 1;

                    const nextControlNo = `${year}-${String(nextNum).padStart(6, "0")}`;
                    setForm(p => ({ ...p, Control_No: nextControlNo }));
                }
            } catch (err) {
                // fallback if API fails
                const year = String(new Date().getFullYear()).slice(-2);
                setForm(p => ({ ...p, Control_No: `${year}-000001` }));
            }
        };
        fetchNextControlNo();
    }, []);
    // ── Fetch departments & users ────────────────────────────────────────────
    useEffect(() => {
        const fetchMaster = async () => {
            try {
                const [dRes, uRes] = await Promise.all([
                    fetch(`${API_BASE_URL}api/Department`),
                    fetch(`${API_BASE_URL}api/Users`),
                ]);
                if (dRes.ok) {
                    const d = await dRes.json();
                    setDepartments(d.filter(x => x.isActive === 1));
                }
                if (uRes.ok) {
                    const u = await uRes.json();
                    setUsers(u.filter(x => x.isActive === 1));
                }
            } catch (err) {
                console.error("Master data fetch error:", err);
            }
        };
        fetchMaster();
    }, []);

    // ── Fetch disposal list ──────────────────────────────────────────────────
    const fetchDisposalList = useCallback(async () => {
        setListLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/DisposalForm`);
            if (res.ok) {
                const data = await res.json();
                setDisposalList(data);
            }
        } catch (err) {
            console.error("Disposal list fetch error:", err);
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "list") fetchDisposalList();
    }, [activeTab, fetchDisposalList]);

    // ── Department Head auto-fill ────────────────────────────────────────────
    const handleDepartmentChange = (value) => {
        handleChange("Department", value);
        handleChange("Department_Head", "");
        if (!value) return;

        const deptObj = departments.find(d => d.departmentName === value);
        if (!deptObj) return;

        const match = users.find(u => {
            const pos = (u.positionId || u.PositionId || "").toString();
            return (
                pos === deptObj.departmentCode ||
                pos.startsWith(deptObj.departmentCode + "/") ||
                pos.startsWith(deptObj.departmentCode + "-") ||
                String(u.departmentId || u.DepartmentId) === String(deptObj.id)
            );
        });

        if (match) {
            handleChange("Department_Head", `${match.firstName} ${match.lastName}`);
        }
    };

    // ── Form helpers ─────────────────────────────────────────────────────────
    const handleChange = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const handleItemChange = (index, field, value) => {
        const updated = [...form.items];
        updated[index][field] = value;
        setForm(p => ({ ...p, items: updated }));
    };

    const addRow = () => setForm(p => ({
        ...p,
        items: [...p.items, { Quantity: "", Unit: "", Item_Name: "", Description: "", Remarks: "" }],
    }));

    const removeRow = (i) => {
        if (form.items.length === 1) return;
        setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
    };

    // ── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!form.Department) {
            Swal.fire({ icon: "warning", title: "Missing Field", text: "Please select a department.", confirmButtonColor: "#2563eb" });
            return;
        }
        if (form.items.some(i => !i.Item_Name || !i.Quantity)) {
            Swal.fire({ icon: "warning", title: "Incomplete Items", text: "Please fill in Item Name and Quantity for all rows.", confirmButtonColor: "#2563eb" });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Submit Disposal Form?",
            html: `<p>Control No: <strong>${form.Control_No}</strong></p><p>Department: <strong>${form.Department}</strong></p><p>Items: <strong>${form.items.length}</strong></p>`,
            showCancelButton: true,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Submit",
            cancelButtonText: "Cancel",
        });
        if (!confirm.isConfirmed) return;

        setLoading(true);
        try {
            const payload = {
                Control_No: form.Control_No,
                Department: form.Department,
                Quantity: form.items.map(i => i.Quantity).join(", "),
                Unit: form.items.map(i => i.Unit).join(", "),
                Item_Name: form.items.map(i => i.Item_Name).join(", "),
                Description: form.items.map(i => i.Description).join(", "),
                Remarks: form.items.map(i => i.Remarks).join(", "),
                PreparedBy: form.Prepared_By || userName,
                Date: form.Date,
                Department_Head: form.Department_Head,
                Item_ReceivedBy: form.Item_ReceivedBy,
                Approved_By: form.Approved_By,
            };

            const res = await fetch(`${API_BASE_URL}api/DisposalForm`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                try {
                    await fetch(`${API_BASE_URL}api/Logs`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            action: "CREATE",
                            module: "Disposal Form",
                            description: `Submitted Disposal Form #${form.Control_No} — Dept: ${form.Department}, Items: ${form.items.length}`,
                            userId: currentUser?.id || currentUser?.userId,
                            username: currentUser?.username,
                            fullName: userName,
                        }),
                    });
                } catch { }

                await Swal.fire({
                    icon: "success",
                    title: "✅ Submitted Successfully!",
                    html: `<strong>${form.Control_No}</strong><br/>Disposal form has been submitted.`,
                    timer: 2500,
                    showConfirmButton: false,
                });
                handleReset();
            } else {
                throw new Error("Server returned an error.");
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: "error", title: "Submission Failed", text: err.message || "Please try again.", confirmButtonColor: "#dc2626" });
        } finally {
            setLoading(false);
        }
    };

    // NEW
    const handleReset = () => {
        setForm({
            Control_No: "", // will be re-fetched
            Department: "",
            Date: new Date().toISOString().split("T")[0],
            items: [{ Quantity: "", Unit: "", Item_Name: "", Description: "", Remarks: "" }],
            Department_Head: "",
            Item_ReceivedBy: "",
            Approved_By: "Justin Choi",
        });
        // re-fetch next control no
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/DisposalForm`);
                if (res.ok) {
                    const data = await res.json();
                    const year = String(new Date().getFullYear()).slice(-2);
                    const thisYearNos = data
                        .map(r => f.controlNo(r))
                        .filter(n => n && n.startsWith(`${year}-`))
                        .map(n => parseInt(n.split("-")[1]) || 0);
                    const nextNum = thisYearNos.length > 0 ? Math.max(...thisYearNos) + 1 : 1;
                    setForm(p => ({ ...p, Control_No: `${year}-${String(nextNum).padStart(6, "0")}` }));
                }
            } catch { }
        })();
    };
    // ── List: filter + paginate ──────────────────────────────────────────────
    const filtered = disposalList.filter(row => {
        const q = search.toLowerCase();
        const matchSearch =
            !q ||
            f.controlNo(row).toLowerCase().includes(q) ||
            f.department(row).toLowerCase().includes(q) ||
            f.preparedBy(row).toLowerCase().includes(q) ||
            f.itemName(row).toLowerCase().includes(q);
        const matchDept = !filterDept || f.department(row) === filterDept;
        const matchDate = !filterDate || f.date(row).startsWith(filterDate);
        const matchStatus = !filterStatus || getStatus(row) === filterStatus;
        return matchSearch && matchDept && matchDate && matchStatus;
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // ── Export to Excel ──────────────────────────────────────────────────────
    const exportToExcel = () => {
        const exportData = filtered.map((row, i) => ({
            "#": i + 1,
            "Control No": f.controlNo(row),
            "Department": f.department(row),
            "Date": f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US") : "",
            "Prepared By": f.preparedBy(row),
            "Item Name": f.itemName(row),
            "Quantity": f.quantity(row),
            "Unit": f.unit(row),
            "Description": f.description(row),
            "Remarks": f.remarks(row),
            "Dept Head": f.deptHead(row),
            "Approved By": f.approvedBy(row),
            "Received By": f.receivedBy(row),
            "Status": STATUS_CONFIG[getStatus(row)].label,
            "Created": f.created(row) ? new Date(f.created(row)).toLocaleString("en-US") : "",
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const colWidths = Object.keys(exportData[0] || {}).map(key => ({
            wch: Math.max(key.length, ...exportData.map(r => String(r[key] || "").length)) + 2,
        }));
        ws["!cols"] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Disposal List");
        XLSX.writeFile(wb, `DisposalList_${new Date().toISOString().split("T")[0]}.xlsx`);
    };

    // ── Styles ───────────────────────────────────────────────────────────────
    const s = {
        page: {
            minHeight: "100vh",
            backgroundColor: "#f1f5f9",
            padding: "28px 48px",
            fontFamily: "'Segoe UI', system-ui, sans-serif",
        },
        card: {
            backgroundColor: "#fff",
            borderRadius: "16px",
            width: "100%",
            margin: "0 auto",
            boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
            overflow: "hidden",
        },
        header: {
            background: "linear-gradient(135deg, #1e40af 0%, #2563eb 60%, #3b82f6 100%)",
            padding: "22px 28px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
        },
        tab: (active) => ({
            padding: "14px 20px", fontSize: "14px",
            fontWeight: active ? "700" : "500",
            color: active ? "#2563eb" : "#6b7280",
            borderBottom: active ? "3px solid #2563eb" : "3px solid transparent",
            marginBottom: "-2px", cursor: "pointer",
            background: "none", border: "none", transition: "all 0.2s",
        }),
        label: {
            fontSize: "12px", fontWeight: "600", color: "#374151",
            textTransform: "uppercase", letterSpacing: "0.6px",
            display: "block", marginBottom: "6px",
        },
        input: {
            border: "1.5px solid #d1d5db", borderRadius: "8px",
            padding: "10px 12px", fontSize: "14px", color: "#1e293b",
            outline: "none", width: "100%", boxSizing: "border-box",
            backgroundColor: "#fff",
        },
        inputRO: {
            border: "1.5px solid #e5e7eb", borderRadius: "8px",
            padding: "10px 12px", fontSize: "14px", color: "#6b7280",
            backgroundColor: "#f9fafb", width: "100%", boxSizing: "border-box",
        },
        select: {
            border: "1.5px solid #d1d5db", borderRadius: "8px",
            padding: "10px 12px", fontSize: "14px", color: "#1e293b",
            outline: "none", width: "100%", boxSizing: "border-box",
            backgroundColor: "#fff", cursor: "pointer",
        },
        th: {
            backgroundColor: "#1e40af", color: "#fff",
            padding: "11px 10px", textAlign: "left",
            fontSize: "11px", fontWeight: "700",
            textTransform: "uppercase", letterSpacing: "0.5px",
            whiteSpace: "nowrap",
        },
        td: {
            padding: "8px 10px", borderBottom: "1px solid #f1f5f9",
            verticalAlign: "middle", fontSize: "13px",
        },
        tInput: {
            border: "1.5px solid #e5e7eb", borderRadius: "6px",
            padding: "7px 8px", fontSize: "13px", width: "100%",
            boxSizing: "border-box", outline: "none", backgroundColor: "#fff",
        },
        tSelect: {
            border: "1.5px solid #e5e7eb", borderRadius: "6px",
            padding: "7px 8px", fontSize: "13px", width: "100%",
            boxSizing: "border-box", outline: "none", backgroundColor: "#fff",
            cursor: "pointer",
        },
        divider: { border: "none", borderTop: "1.5px solid #f1f5f9", margin: "0 0 24px 0" },
        sectionTitle: {
            fontSize: "15px", fontWeight: "700", color: "#1e293b",
            borderLeft: "4px solid #2563eb", paddingLeft: "12px", marginBottom: "16px",
        },
    };

    const paginBtn = (disabled) => ({
        padding: "6px 10px", borderRadius: "6px", fontSize: "13px",
        fontWeight: "600", border: "1.5px solid #e5e7eb",
        backgroundColor: disabled ? "#f9fafb" : "#fff",
        color: disabled ? "#d1d5db" : "#374151",
        cursor: disabled ? "not-allowed" : "pointer",
    });

    return (
        <div style={s.page}>

            {/* ── VIEW MODAL ── */}
            {viewRow && (
                <ViewModal row={viewRow} onClose={() => setViewRow(null)} />
            )}

            <div style={s.card}>

                {/* ── HEADER ── */}
                <div style={s.header}>
                    <div>
                        <h2 style={{ color: "#fff", fontWeight: "800", fontSize: "22px", margin: "0 0 4px 0" }}>
                            Item Disposal Form
                        </h2>
                        <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", margin: 0 }}>
                            Mega Soft Hygienic Products, Inc. · Spareparts Department
                        </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
                        <span style={{
                            backgroundColor: "rgba(255,255,255,0.2)", color: "#fff",
                            borderRadius: "8px", padding: "7px 16px",
                            fontSize: "14px", fontWeight: "700",
                            border: "1px solid rgba(255,255,255,0.3)", letterSpacing: "0.5px",
                        }}>
                            {form.Control_No}
                        </span>
                        <span style={{
                            backgroundColor: "rgba(255,255,255,0.12)",
                            color: "rgba(255,255,255,0.85)",
                            borderRadius: "6px", padding: "4px 12px", fontSize: "12px",
                        }}>
                            📅 {today}
                        </span>
                    </div>
                </div>

                {/* ── TABS ── */}
                <div style={{ display: "flex", borderBottom: "2px solid #e5e7eb", padding: "0 28px", backgroundColor: "#fafafa" }}>
                    <button style={s.tab(activeTab === "form")} onClick={() => setActiveTab("form")}>📋 Form Details</button>
                    <button style={s.tab(activeTab === "preview")} onClick={() => setActiveTab("preview")}>👁 Preview</button>
                    <button style={s.tab(activeTab === "list")} onClick={() => setActiveTab("list")}>📄 Disposal List</button>
                </div>

                {/* ── BODY ── */}
                <div style={{ padding: "28px" }}>

                    {/* ═══════════════ FORM TAB ═══════════════ */}
                    {activeTab === "form" && (
                        <>
                            <div style={s.sectionTitle}>Disposal Information</div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                                <div>
                                    <label style={s.label}>Control Number</label>
                                    <input style={s.inputRO} value={form.Control_No} readOnly />
                                </div>
                                <div>
                                    <label style={s.label}>Request Date</label>
                                    <input style={s.inputRO} value={today} readOnly />
                                </div>
                                <div>
                                    <label style={s.label}>Date <span style={{ color: "#ef4444" }}>*</span></label>
                                    <input type="date" style={s.input} value={form.Date}
                                        onChange={e => handleChange("Date", e.target.value)} />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                                <div>
                                    <label style={s.label}>Department <span style={{ color: "#ef4444" }}>*</span></label>
                                    <select style={s.select} value={form.Department}
                                        onChange={e => handleDepartmentChange(e.target.value)}>
                                        <option value="">-- Select Department --</option>
                                        {departments.map(d => (
                                            <option key={d.id} value={d.departmentName}>{d.departmentName}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <label style={s.label}>Prepared By <span style={{ color: "#ef4444" }}>*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Spareparts personnel name..."
                                        value={form.Prepared_By ?? ""}
                                        onChange={e => handleChange("Prepared_By", e.target.value)}
                                        style={{ ...s.input, textAlign: "center" }}
                                    />
                                    <span style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center", fontStyle: "italic" }}>
                                        Spare Parts Personnel
                                    </span>
                                </div>
                            </div>

                            <hr style={s.divider} />

                            <div style={s.sectionTitle}>Items for Disposal</div>
                            <div style={{ overflowX: "auto", marginBottom: "10px" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                        <tr>
                                            {["#", "Qty", "Unit", "Item Name", "Description", "Remarks", ""].map((h, i) => (
                                                <th key={i} style={{
                                                    ...s.th,
                                                    borderRadius: i === 0 ? "8px 0 0 0" : i === 6 ? "0 8px 0 0" : 0,
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.items.map((item, idx) => (
                                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                                <td style={{ ...s.td, textAlign: "center", color: "#94a3b8", fontWeight: "600", width: "40px" }}>
                                                    {idx + 1}
                                                </td>
                                                <td style={{ ...s.td, width: "80px" }}>
                                                    <input type="number" min="0" value={item.Quantity}
                                                        onChange={e => handleItemChange(idx, "Quantity", e.target.value)}
                                                        style={{ ...s.tInput, textAlign: "center" }} placeholder="0" />
                                                </td>
                                                <td style={{ ...s.td, width: "120px" }}>
                                                    <select value={item.Unit}
                                                        onChange={e => handleItemChange(idx, "Unit", e.target.value)}
                                                        style={s.tSelect}>
                                                        <option value=""></option>
                                                        {UNITS.map(u => (
                                                            <option key={u} value={u}>{u}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td style={s.td}>
                                                    <input type="text" value={item.Item_Name}
                                                        onChange={e => handleItemChange(idx, "Item_Name", e.target.value)}
                                                        style={s.tInput} placeholder="Item name..." />
                                                </td>
                                                <td style={s.td}>
                                                    <input type="text" value={item.Description}
                                                        onChange={e => handleItemChange(idx, "Description", e.target.value)}
                                                        style={s.tInput} placeholder="OLD / DAMAGED" />
                                                </td>
                                                <td style={s.td}>
                                                    <input type="text" value={item.Remarks}
                                                        onChange={e => handleItemChange(idx, "Remarks", e.target.value)}
                                                        style={s.tInput} placeholder="DISPOSE" />
                                                </td>
                                                <td style={{ ...s.td, textAlign: "center", width: "40px" }}>
                                                    <button onClick={() => removeRow(idx)}
                                                        disabled={form.items.length === 1}
                                                        style={{
                                                            background: form.items.length === 1 ? "#f1f5f9" : "#fee2e2",
                                                            color: form.items.length === 1 ? "#cbd5e1" : "#dc2626",
                                                            border: "none", borderRadius: "6px",
                                                            width: "28px", height: "28px",
                                                            cursor: form.items.length === 1 ? "not-allowed" : "pointer",
                                                            fontWeight: "700", fontSize: "13px",
                                                        }}>✕</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button onClick={addRow} style={{
                                padding: "7px 18px", backgroundColor: "#eff6ff",
                                color: "#2563eb", border: "1.5px dashed #93c5fd",
                                borderRadius: "7px", cursor: "pointer",
                                fontSize: "13px", fontWeight: "600", marginBottom: "24px",
                            }}>+ Add Row</button>

                            <hr style={s.divider} />

                            <div style={s.sectionTitle}>Signatories</div>
                            <div style={{
                                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                                gap: "20px", padding: "20px 24px",
                                backgroundColor: "#f8fafc", borderRadius: "12px",
                                border: "1px solid #e2e8f0", marginBottom: "16px",
                            }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <label style={s.label}>Prepared By</label>
                                    <div style={{ borderBottom: "1.5px solid #334155", paddingBottom: "6px", marginBottom: "4px", textAlign: "center" }}>
                                        <span style={{ fontSize: "14px", fontWeight: "700", color: "#2563eb" }}>{userName}</span>
                                    </div>
                                    <span style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center", fontStyle: "italic" }}>
                                        Spare Parts Personnel
                                    </span>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <label style={s.label}>
                                        Department Head <span style={{ color: "#ef4444" }}>*</span>
                                        {form.Department && !form.Department_Head && (
                                            <span style={{ marginLeft: "6px", fontSize: "10px", color: "#f59e0b", fontWeight: "500" }}>
                                                (No head found)
                                            </span>
                                        )}
                                    </label>
                                    <input type="text" placeholder="Full name..." value={form.Department_Head}
                                        onChange={e => handleChange("Department_Head", e.target.value)}
                                        style={{ ...s.input, textAlign: "center" }} />
                                    {form.Department_Head && (
                                        <span style={{ fontSize: "11px", color: "#16a34a", textAlign: "center", fontWeight: "600" }}>
                                            ✓ Auto-filled: {form.Department_Head}
                                        </span>
                                    )}
                                    <span style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center", fontStyle: "italic" }}>
                                        Signature over Printed Name
                                    </span>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <label style={s.label}>Approved By <span style={{ color: "#ef4444" }}>*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Full name..."
                                        value={form.Approved_By}
                                        onChange={e => handleChange("Approved_By", e.target.value)}
                                        style={{ ...s.input, textAlign: "center" }}
                                    />
                                    <span style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center", fontStyle: "italic" }}>
                                        Authorizing Signatory
                                    </span>
                                </div>
                            </div>

                            <div style={{ maxWidth: "340px" }}>
                                <label style={s.label}>Item Received By <span style={{ color: "#ef4444" }}>*</span></label>
                                <input type="text" placeholder="Scrap officer name..." value={form.Item_ReceivedBy}
                                    onChange={e => handleChange("Item_ReceivedBy", e.target.value)}
                                    style={s.input} />
                                <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>
                                    Scrap Officer In-Charge
                                </span>
                            </div>
                        </>
                    )}

                    {/* ═══════════════ PREVIEW TAB ═══════════════ */}
                    {activeTab === "preview" && (
                        <div>
                            <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                                Preview of the form data to be submitted:
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "20px" }}>
                                <tbody>
                                    {[
                                        ["Control No", form.Control_No],
                                        ["Department", form.Department || "—"],
                                        ["Date", form.Date || "—"],
                                        ["Prepared By", userName],
                                        ["Department Head", form.Department_Head || "—"],
                                        ["Approved By", form.Approved_By || "—"],
                                        ["Item Received By", form.Item_ReceivedBy || "—"],
                                    ].map(([k, v]) => (
                                        <tr key={k} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                            <td style={{ padding: "10px 12px", fontWeight: "600", color: "#374151", width: "35%", backgroundColor: "#f8fafc" }}>{k}</td>
                                            <td style={{ padding: "10px 12px", color: "#1e293b" }}>{v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={{ fontWeight: "600", color: "#374151", marginBottom: "8px" }}>Items:</div>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr>
                                        {["#", "Qty", "Unit", "Item Name", "Description", "Remarks"].map(h => (
                                            <th key={h} style={s.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {form.items.map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                            <td style={s.td}>{idx + 1}</td>
                                            <td style={s.td}>{item.Quantity || "—"}</td>
                                            <td style={s.td}>{item.Unit || "—"}</td>
                                            <td style={s.td}>{item.Item_Name || "—"}</td>
                                            <td style={s.td}>{item.Description || "—"}</td>
                                            <td style={s.td}>{item.Remarks || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ═══════════════ LIST TAB ═══════════════ */}
                    {activeTab === "list" && (
                        <div>
                            {/* Toolbar */}
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                                <div style={{ position: "relative", flex: "1", minWidth: "220px" }}>
                                    <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "15px" }}>🔍</span>
                                    <input
                                        type="text"
                                        placeholder="Search control no, dept, prepared by, item..."
                                        value={search}
                                        onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                        style={{ ...s.input, paddingLeft: "36px" }}
                                    />
                                </div>

                                <select value={filterDept}
                                    onChange={e => { setFilterDept(e.target.value); setCurrentPage(1); }}
                                    style={{ ...s.select, width: "auto", minWidth: "160px" }}>
                                    <option value="">All Departments</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.departmentName}>{d.departmentName}</option>
                                    ))}
                                </select>

                                <select value={filterStatus}
                                    onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                                    style={{ ...s.select, width: "auto", minWidth: "150px" }}>
                                    <option value="">All Statuses</option>
                                    <option value="pending">⏳ Pending</option>
                                    <option value="approved">✅ Approved</option>
                                    <option value="disapproved">❌ Disapproved</option>
                                </select>

                                <input type="date" value={filterDate}
                                    onChange={e => { setFilterDate(e.target.value); setCurrentPage(1); }}
                                    style={{ ...s.input, width: "auto" }}
                                    title="Filter by date" />

                                {(search || filterDept || filterDate || filterStatus) && (
                                    <button onClick={() => { setSearch(""); setFilterDept(""); setFilterDate(""); setFilterStatus(""); setCurrentPage(1); }}
                                        style={{
                                            padding: "10px 14px", borderRadius: "8px",
                                            border: "1.5px solid #fca5a5", backgroundColor: "#fff",
                                            color: "#dc2626", fontWeight: "600", cursor: "pointer", fontSize: "13px",
                                            whiteSpace: "nowrap",
                                        }}>✕ Clear</button>
                                )}

                                <button onClick={exportToExcel}
                                    style={{
                                        padding: "10px 18px", borderRadius: "8px",
                                        border: "none", backgroundColor: "#16a34a",
                                        color: "#fff", fontWeight: "700", cursor: "pointer", fontSize: "13px",
                                        display: "flex", alignItems: "center", gap: "6px",
                                        boxShadow: "0 2px 8px rgba(22,163,74,0.25)",
                                        whiteSpace: "nowrap",
                                    }}>📊 Export Excel</button>

                                <button onClick={fetchDisposalList}
                                    style={{
                                        padding: "10px 14px", borderRadius: "8px",
                                        border: "1.5px solid #d1d5db", backgroundColor: "#fff",
                                        color: "#374151", fontWeight: "600", cursor: "pointer", fontSize: "13px",
                                    }}>🔄</button>
                            </div>

                            {/* Record count */}
                            {!listLoading && (
                                <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "10px" }}>
                                    {filtered.length} record{filtered.length !== 1 ? "s" : ""} found
                                    {(search || filterDept || filterDate || filterStatus) && (
                                        <span style={{ color: "#2563eb", fontWeight: "600" }}> (filtered)</span>
                                    )}
                                </div>
                            )}

                            {/* Table */}
                            {listLoading ? (
                                <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8", fontSize: "15px" }}>
                                    ⏳ Loading disposal records...
                                </div>
                            ) : paginated.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8", fontSize: "15px" }}>
                                    {disposalList.length > 0 ? "No records match your search." : "No disposal records found."}
                                </div>
                            ) : (
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                        <thead>
                                            <tr>
                                                {/* ── Added "Action" column at the end ── */}
                                                {["#", "Control No", "Department", "Date", "Prepared By", "Item Name", "Qty", "Unit", "Description", "Remarks", "Dept Head", "Approved By", "Received By", "Status", "Action"].map((h, i, arr) => (
                                                    <th key={i} style={{
                                                        ...s.th,
                                                        borderRadius: i === 0 ? "8px 0 0 0" : i === arr.length - 1 ? "0 8px 0 0" : 0,
                                                        textAlign: h === "Action" ? "center" : "left",
                                                    }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.map((row, idx) => (
                                                <tr key={row.id || idx}
                                                    style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc", transition: "background 0.15s" }}
                                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = "#eff6ff"}
                                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#fff" : "#f8fafc"}
                                                >
                                                    <td style={{ ...s.td, textAlign: "center", color: "#94a3b8", width: "40px" }}>
                                                        {(currentPage - 1) * PAGE_SIZE + idx + 1}
                                                    </td>
                                                    <td style={{ ...s.td, fontWeight: "700", color: "#1e40af", whiteSpace: "nowrap" }}>
                                                        {f.controlNo(row) || "—"}
                                                    </td>
                                                    <td style={s.td}>{f.department(row) || "—"}</td>
                                                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                                                        {f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US") : "—"}
                                                    </td>
                                                    <td style={s.td}>{f.preparedBy(row) || "—"}</td>
                                                    <td style={s.td}>{f.itemName(row) || "—"}</td>
                                                    <td style={{ ...s.td, textAlign: "center" }}>{f.quantity(row) || "—"}</td>
                                                    <td style={{ ...s.td, textAlign: "center" }}>{f.unit(row) || "—"}</td>
                                                    <td style={s.td}>{f.description(row) || "—"}</td>
                                                    <td style={s.td}>{f.remarks(row) || "—"}</td>
                                                    <td style={s.td}>{f.deptHead(row) || "—"}</td>
                                                    <td style={s.td}>{f.approvedBy(row) || "—"}</td>
                                                    <td style={s.td}>{f.receivedBy(row) || "—"}</td>
                                                    <td style={s.td}>
                                                        <StatusBadge row={row} />
                                                    </td>
                                                    {/* ── VIEW BUTTON ── */}
                                                    <td style={{ ...s.td, textAlign: "center", whiteSpace: "nowrap" }}>
                                                        <button
                                                            onClick={() => setViewRow(row)}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: "4px",
                                                                padding: "5px 12px",
                                                                backgroundColor: "#eff6ff",
                                                                color: "#2563eb",
                                                                border: "1.5px solid #bfdbfe",
                                                                borderRadius: "6px",
                                                                cursor: "pointer",
                                                                fontSize: "12px",
                                                                fontWeight: "700",
                                                                transition: "all 0.15s",
                                                            }}
                                                            onMouseEnter={e => {
                                                                e.currentTarget.style.backgroundColor = "#2563eb";
                                                                e.currentTarget.style.color = "#fff";
                                                            }}
                                                            onMouseLeave={e => {
                                                                e.currentTarget.style.backgroundColor = "#eff6ff";
                                                                e.currentTarget.style.color = "#2563eb";
                                                            }}
                                                        >
                                                            👁 View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {!listLoading && filtered.length > PAGE_SIZE && (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
                                    <span style={{ fontSize: "13px", color: "#64748b" }}>
                                        Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                                    </span>
                                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={paginBtn(currentPage === 1)}>«</button>
                                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={paginBtn(currentPage === 1)}>‹</button>

                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let page;
                                            if (totalPages <= 5) page = i + 1;
                                            else if (currentPage <= 3) page = i + 1;
                                            else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                            else page = currentPage - 2 + i;
                                            return (
                                                <button key={page} onClick={() => setCurrentPage(page)} style={{
                                                    padding: "6px 12px", borderRadius: "6px", fontSize: "13px",
                                                    fontWeight: page === currentPage ? "700" : "500",
                                                    border: page === currentPage ? "none" : "1.5px solid #e5e7eb",
                                                    backgroundColor: page === currentPage ? "#2563eb" : "#fff",
                                                    color: page === currentPage ? "#fff" : "#374151",
                                                    cursor: "pointer",
                                                }}>{page}</button>
                                            );
                                        })}

                                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={paginBtn(currentPage === totalPages)}>›</button>
                                        <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={paginBtn(currentPage === totalPages)}>»</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── FOOTER ── */}
                {activeTab !== "list" && (
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 28px", borderTop: "1px solid #e5e7eb", backgroundColor: "#f8fafc",
                    }}>
                        <p style={{ fontSize: "10px", color: "#9ca3af", fontStyle: "italic", margin: 0, maxWidth: "500px" }}>
                            This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
                        </p>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button onClick={handleReset} style={{
                                padding: "10px 22px", borderRadius: "8px",
                                border: "1.5px solid #d1d5db", backgroundColor: "#fff",
                                color: "#374151", fontWeight: "600", cursor: "pointer", fontSize: "14px",
                            }}>Reset</button>
                            <button onClick={handleSubmit} disabled={loading} style={{
                                padding: "10px 28px", borderRadius: "8px", border: "none",
                                backgroundColor: loading ? "#93c5fd" : "#2563eb",
                                color: "#fff", fontWeight: "700",
                                cursor: loading ? "not-allowed" : "pointer", fontSize: "14px",
                                boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                            }}>
                                {loading ? "Submitting..." : "Submit Form"}
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Disposal_Form;